// Copyright © 2021 Move Closer
import { __decorate } from "tslib";
import { Component, Inject, Prop, Vue } from 'vue-property-decorator';
import { BootstrapButton, BootstrapImage } from '../../../../dsl/front';
import { isRelated } from '../../../../services';
import { PlayIcon } from '../../../_components/ui';
import { prepareImage } from '../../../_filters';
import VideoPlayer from '../../../_components/VideoPlayer/VideoPlayer';
/**
 * Component capable of rendering the `EmbedModule`
 * with the version set to `EmbedVersion.YouTube`.
 *
 * @author Stanisław Gregor <stanislaw.gregor@movecloser.pl>
 */
let EmbedYouTubeUi = class EmbedYouTubeUi extends Vue {
    constructor() {
        super(...arguments);
        /**
         * Determines whether the YouTube player has been initialised.
         */
        this.isPlayerInitialised = false;
        /**
         * YouTube player instance.
         */
        this.player = null;
        /**
         * Ready to use `Image` object, resolved using `RelatedService`.
         */
        this.resolvedThumbnail = null;
        /**
         * Very important for SSR.
         */
        this.videoAttrId = this.$id() + 'video';
    }
    get image() {
        if (this.resolvedThumbnail === null) {
            return this.youTubeCover;
        }
        return this.resolvedThumbnail;
    }
    get youTubeCover() {
        return {
            src: `https://img.youtube.com/vi/${this.videoId}/0.jpg`,
            alt: 'YouTube cover video',
            width: 480,
            height: 360
        };
    }
    get baseVideoMetadata() {
        return {
            '@context': 'https://schema.org',
            '@type': 'VideoObject',
            name: this.description ? this.description : this.parentTitle,
            thumbnailUrl: this.image.src,
            uploadDate: this.publicationDate,
            embedUrl: `https://www.youtube.com/embed/${this.videoId}`
        };
    }
    get video() {
        return {
            url: `https://www.youtube.com/embed/${this.videoId}`
        };
    }
    /**
     * @inheritDoc
     */
    async fetchRelated() {
        await this.resolveThumbnail();
    }
    /**
     * Determines whether the `videoId` prop has been defined and has content.
     */
    get hasVideoId() {
        return typeof this.videoId === 'string' && this.videoId.length > 0;
    }
    onVideoInit() {
        this.isPlayerInitialised = true;
    }
    /**
     * Dispatch when mouse is over YT.
     */
    onMouseEnter() {
        if (!this.autoplay || this.isPlayerInitialised) {
            return;
        }
        this.eventBus.emit('ui:video-player.play', {
            playerId: this.videoAttrId,
            autoplay: true,
            muted: true
        });
    }
    /**
     * Handles the `@click` event on the "play" button.
     */
    onPlayBtnClick() {
        this.eventBus.emit('ui:video-player.play', {
            playerId: this.videoAttrId,
            autoplay: true
        });
    }
    /**
     * Resolves the thumbnail image, if any.
     */
    async resolveThumbnail() {
        if (typeof this.thumbnail === 'undefined' || !isRelated(this.thumbnail)) {
            return;
        }
        let imageFile;
        try {
            imageFile = await this.relatedService.resolve(this.thumbnail);
        }
        catch (e) {
            console.warn(e);
            return;
        }
        const maxWidth = this.isMobile ? 768 : 1220;
        this.resolvedThumbnail = prepareImage(imageFile, this.imageRatio, maxWidth, this.isLazy);
    }
};
__decorate([
    Prop({
        type: Boolean,
        required: false
    })
], EmbedYouTubeUi.prototype, "autoplay", void 0);
__decorate([
    Prop({
        type: String,
        required: false
    })
], EmbedYouTubeUi.prototype, "description", void 0);
__decorate([
    Prop({
        type: String,
        required: true
    })
], EmbedYouTubeUi.prototype, "imageRatio", void 0);
__decorate([
    Prop({
        type: Object,
        required: true
    })
], EmbedYouTubeUi.prototype, "relatedService", void 0);
__decorate([
    Prop({
        type: Object,
        required: false
    })
], EmbedYouTubeUi.prototype, "thumbnail", void 0);
__decorate([
    Prop({
        type: Boolean,
        required: false,
        default: true
    })
], EmbedYouTubeUi.prototype, "isLazy", void 0);
__decorate([
    Prop({
        type: Boolean,
        required: false,
        default: true
    })
], EmbedYouTubeUi.prototype, "isMobile", void 0);
__decorate([
    Prop({
        type: String,
        required: true
    })
], EmbedYouTubeUi.prototype, "origin", void 0);
__decorate([
    Prop({
        type: String,
        required: false,
        default: ''
    })
], EmbedYouTubeUi.prototype, "parentTitle", void 0);
__decorate([
    Prop({
        type: String,
        required: false,
        default: ''
    })
], EmbedYouTubeUi.prototype, "publicationDate", void 0);
__decorate([
    Prop({
        type: String,
        required: true
    })
], EmbedYouTubeUi.prototype, "videoId", void 0);
__decorate([
    Prop({
        type: Object,
        required: true
    })
], EmbedYouTubeUi.prototype, "windowService", void 0);
__decorate([
    Inject({ from: 'eventBus' })
], EmbedYouTubeUi.prototype, "eventBus", void 0);
EmbedYouTubeUi = __decorate([
    Component({
        name: 'EmbedYouTubeUi',
        components: {
            BootstrapButton,
            BootstrapImage,
            PlayIcon,
            VideoPlayer
        },
        async created() {
            await this.fetchRelated();
        },
        mounted() {
            if (this.$refs.video && this.$refs.video instanceof Element) {
                this.videoAttrId = this.$refs.video.getAttribute('id');
            }
            this.$emit('ready');
        },
        template: `
    <figure v-if="hasVideoId" class="embed-youtube">

    <div class="embed-youtube__content-wrapper" @mouseenter="onMouseEnter">

      <VideoPlayer class="embed-youtube__player" :video="video" :videoAttrId="videoAttrId" forceType="youtube"
                   :baseVideoMetadata="baseVideoMetadata" :eventBus="eventBus" :origin="origin"
                   @init="onVideoInit"/>

      <BootstrapImage v-bind="image" class="embed-youtube__thumbnail"
                      :class="{ '--hidden': isPlayerInitialised }"/>

      <BootstrapButton @click="onPlayBtnClick" class="embed-youtube__play-btn shadow-none"
                       :class="{ '--hidden': isPlayerInitialised }">
        <PlayIcon/>
      </BootstrapButton>
    </div>

    <figcaption v-if="description" class="embed-youtube__desc small">
      {{ description }}
    </figcaption>
    </figure>
    <div v-else class="empty-div"></div>
  `
    })
], EmbedYouTubeUi);
export { EmbedYouTubeUi };
